import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth";
import {
  getClient,
  getClientByEmail,
  getClientPrimaryContact,
  getSimpleClient,
} from "../../services/clientService";
import {
  getReferenceQuestionnaireQuestions,
  getReferenceQuestionnaireQuestionOptions,
  getReferenceQuestionnaireBase,
  saveFinishedReferenceQuestionnaire,
  getCleanSignature,
} from "../../services/referenceQuestionnaireService";
import { ClientPrimaryContact, SimpleClient } from "../../interfaces/client";
import { ReferenceQuestionnaire } from "../../interfaces/referenceQuestionnaire";
import { QuestionOption } from "../../interfaces/questionOption";
import { AdditionalForm } from "../../interfaces/additionalForm";
import { HttpService } from "../../services/httpService";
import { getAdditionalForm } from "../../services/additionalFormService";
import EditBaseText from "./EditBaseText";
import { useNavigate, useParams } from "react-router-dom";
import EditQuestion from "./EditQuestion";
import AddQuestion from "./AddQuestion";
import AuthorizationForm, {
  Authorization,
} from "../form-components/AuthorizationForm";
import { getReferenceAccessByToken } from "../../services/referenceFormAccessService";
import { getSimpleReferenceDetailByID } from "../../services/referenceDetailService";
import {
  getSimpleOrderByID,
  updateOrderStatus,
} from "../../services/orderService";
import { getSimpleReferenceOrderByID } from "../../services/referenceOrderService";
import { SimpleOrder } from "../../interfaces/order";
import { SimpleReferenceDetail } from "../../interfaces/referenceDetaill";
import { ReferenceFormAccess } from "../../interfaces/referenceFormAccesss";
import { formatDate } from "../../services/helpers";
import { getOrderFormByOrderIDType } from "../../services/orderFormService";
import ToolTip from "../component-tools/ToolTip";
import { SimpleReferenceOrder } from "../../interfaces/referenceOrder";
import ConfirmPopup from "../client-components/ConfirmPopup";
import "./ReferenceForm.css";

interface ReferenceFormProps {
  isEditing: boolean;
}

const ReferenceForm: React.FC<ReferenceFormProps> = ({ isEditing }) => {
  const { decodeToken } = useAuth();
  const { clientID, token } = useParams();
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [primaryContact, setPrimaryContact] =
    useState<ClientPrimaryContact | null>(null);
  const [questionsWithOptions, setQuestionsWithOptions] = useState<
    Map<number, { questionText: string; options: QuestionOption[] }>
  >(new Map());
  const [referenceQuestionnaire, setReferenceQuestionnaire] =
    useState<ReferenceQuestionnaire>();
  const [signatureForm, setSignatureForm] = useState<AdditionalForm | null>(
    null,
  );
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [showEditBaseText, setShowEditBaseText] = useState(false);
  const [editingBaseText, setEditingBaseText] = useState<{
    key: string;
    text: string;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<{
    id: number;
    text: string;
    type: "question" | "option";
  } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [comments, setComments] = useState<string>("");
  const navigate = useNavigate();
  const [order, setOrder] = useState<SimpleOrder>();
  const [referenceDetail, setReferenceDetail] =
    useState<SimpleReferenceDetail>();
  const [referenceFormAccess, setReferenceFormAccess] =
    useState<ReferenceFormAccess>();
  const [capacityComments, setCapacityComments] = useState<string>("");
  const [noKnowledgeComments, setNoKnowledgeComments] = useState<string>("");
  const [completingForm, setCompletingForm] = useState(false);
  const [pageDecorationUrl, setPageDecorationUrl] = useState<string | null>(
    null,
  );
  const [pageDecorationForm, setPageDecorationForm] =
    useState<AdditionalForm | null>(null);
  const [referenceOrder, setReferenceOrder] = useState<SimpleReferenceOrder>();
  const [hiddenYesNoQuestionIds, setHiddenYesNoQuestionIds] = useState<
    Set<number>
  >(new Set());
  const [showConfirmationSelectXAll, setShowConfirmationSelectXAll] =
    useState(false);
  const [showConfirmationSelectNoAll, setShowConfirmationSelectNoAll] =
    useState(false);
  const [sendCopy, setSendCopy] = useState(false);
  const [sendCopyEmail, setSendCopyEmail] = useState<string>("");

  useEffect(() => {
    const loadClientAndQuestions = async () => {
      try {
        let clientResponse;
        let orderResponse;

        if (token) {
          const referenceFormAccessResponse =
            await getReferenceAccessByToken(token);

          if (!referenceFormAccessResponse) {
            console.error("Invalid or expired token.");
            navigate("/expired");
            return;
          }
          setReferenceFormAccess(referenceFormAccessResponse);

          const nowDate = new Date().getTime();
          const expiryTimestamp = new Date(
            referenceFormAccessResponse.expiryDate,
          ).getTime();

          if (referenceFormAccessResponse.status === "submitted") {
            console.error("Form already submitted.");
            navigate("/already-submitted");
            return;
          }
          if (expiryTimestamp < nowDate) {
            console.error("Token has expired.");
            navigate("/expired");
            return;
          }

          const referenceDetailResponse = await getSimpleReferenceDetailByID(
            referenceFormAccessResponse.referenceDetailID,
          );
          setReferenceDetail(referenceDetailResponse);
          setSendCopyEmail(referenceDetailResponse.referenceEmail ?? "");

          const referenceOrderResponse = await getSimpleReferenceOrderByID(
            referenceDetailResponse.referenceOrderID,
          );

          setReferenceOrder(referenceOrderResponse);

          orderResponse = await getSimpleOrderByID(
            referenceOrderResponse.orderID,
          );
          setOrder(orderResponse);

          clientResponse = await getSimpleClient(orderResponse.clientID);
          setClient(clientResponse);

          setCompletingForm(true);

          const clientPrimaryContact = await getClientPrimaryContact(
            orderResponse.clientID,
          );
          setPrimaryContact(clientPrimaryContact);
        } else if (clientID) {
          const numericClientID = parseInt(clientID, 10);
          clientResponse = await getClient(numericClientID);
          setClient(clientResponse);
          const clientPrimaryContact = await getClientPrimaryContact(
            clientResponse.clientID,
          );
          setPrimaryContact(clientPrimaryContact);
        } else {
          const email = decodeToken(localStorage.getItem("token") || "");
          const clientIDString = localStorage.getItem("clientID");
          const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
          clientResponse = await getClientByEmail(email, clientID);
          setClient(clientResponse);

          const clientPrimaryContact = await getClientPrimaryContact(
            clientResponse.clientID,
          );
          setPrimaryContact(clientPrimaryContact);
        }

        if (clientResponse) {
          const signatureForm = await getAdditionalForm(
            clientResponse.clientID,
            1,
          );
          setSignatureForm(signatureForm);

          if (signatureForm) {
            const signatureUrl = await HttpService.getFile(
              "signatures",
              clientResponse.clientID.toString(),
              signatureForm.completedFormUrl,
            );
            const cleanedSignatureDataUrl =
              await getCleanSignature(signatureUrl);
            setSignatureUrl(cleanedSignatureDataUrl);
          }

          const pageDecorationForm = await getAdditionalForm(
            clientResponse.clientID,
            5,
          );
          setPageDecorationForm(pageDecorationForm);

          if (pageDecorationForm) {
            const pageDecorationUrl = await HttpService.getFile(
              "page-decorations",
              clientResponse.clientID.toString(),
              pageDecorationForm.completedFormUrl,
            );
            // const cleanedPageDecorationDataUrl = await getCleanSignature(
            //   pageDecorationUrl
            // );
            setPageDecorationUrl(pageDecorationUrl);
          }

          if (orderResponse) {
            const authForm = await getOrderFormByOrderIDType(
              orderResponse.orderID,
              5,
            );
            if (authForm) {
              const authUrl = await HttpService.getFile(
                "reference-screening-authorizations",
                authForm.orderFormID.toString(),
                authForm.completedFormUrl,
              );
              setAuthUrl(authUrl);
            }
          }

          const referenceQuestionnaireResponse =
            await getReferenceQuestionnaireBase(clientResponse.clientID);

          setReferenceQuestionnaire(referenceQuestionnaireResponse);

          if (referenceQuestionnaireResponse.referenceQuestionnaireID) {
            const questions = await getReferenceQuestionnaireQuestions(
              referenceQuestionnaireResponse.referenceQuestionnaireID,
            );

            const lastQuestionId = questions[questions.length - 1]?.questionID;

            const questionsWithOptionsMap = new Map<
              number,
              { questionText: string; options: QuestionOption[] }
            >();

            const initialResponses: Record<string, string> = {};

            for (const question of questions) {
              const options = (
                await getReferenceQuestionnaireQuestionOptions(
                  question.questionID,
                )
              ).sort((a, b) => a.questionOptionID - b.questionOptionID);

              questionsWithOptionsMap.set(question.questionID, {
                questionText: question.questionText,
                options: options,
              });

              if (options.length > 0) {
                options.forEach((option) => {
                  initialResponses[`o-${option.questionOptionID}`] = "";
                });
              } else {
                if (question.questionID === lastQuestionId) {
                  initialResponses[`q-${question.questionID}`] = "No";
                } else {
                  initialResponses[`q-${question.questionID}`] = "";
                }
              }
            }

            setQuestionsWithOptions(questionsWithOptionsMap);
            setResponses(initialResponses);
            setHiddenYesNoQuestionIds(
              new Set(lastQuestionId ? [lastQuestionId] : []),
            );
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        navigate("/error", {
          state: {
            message: `Something went wrong while loading the reference form. Please try again later. Error: ${error}`,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    loadClientAndQuestions();
  }, [clientID, decodeToken, navigate, token]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAuthorizationChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (e.target.type === "date" && field === "dated") {
      value = new Date(e.target.value).toISOString();
    }

    setAuthorization((prev) => ({
      ...prev,
      [field]: value,
    }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const handleCommentsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    type: string,
  ) => {
    if (type === "comments") {
      setComments(e.target.value);
      setValidationErrors((prevErrors) => ({ ...prevErrors, comments: "" }));
    } else if (type === "capacity") {
      setCapacityComments(e.target.value);
    } else if (type === "noKnowledge") {
      setNoKnowledgeComments(e.target.value);
    }
  };

  const handleResponseChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    let value: string =
      e.target.type === "radio" ? e.target.value : e.target.value;

    setResponses((prev) => ({
      ...prev,
      [key]: value,
    }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, [key]: "" }));
  };

  const handleSendCopyChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    if (type === "sendCopyEmail") {
      setSendCopyEmail(e.target.value);
      setValidationErrors((prevErrors) => ({ ...prevErrors, sendCopy: "" }));
    }
  };

  const handleSelectAll = (value: string) => {
    setResponses((prev) => {
      const allSelected = Object.values(prev).every((val) => val === value);
      const updatedResponses: Record<string, string> = {};

      const hiddenQuestionKeys = new Set<string>();
      hiddenYesNoQuestionIds.forEach((id) => {
        hiddenQuestionKeys.add(`q-${id}`);
      });

      Object.keys(prev).forEach((key) => {
        if (hiddenQuestionKeys.has(key)) {
          updatedResponses[key] = "No";
        } else {
          updatedResponses[key] = allSelected ? "" : value;
        }
      });

      return updatedResponses;
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        updatedErrors[key] = "";
      });
      return updatedErrors;
    });
  };

  const validateFields = () => {
    const errors: { [key: string]: string } = {};
    let hasYesAnswer = false;
    const isValidEmail = (email: string) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    Object.keys(responses).forEach((key) => {
      const value = responses[key];
      if (!value) {
        errors.questions = "Please answer all questions.";
      }
      if (value === "Yes") {
        hasYesAnswer = true;
      }
    });

    if (hasYesAnswer && comments === "") {
      errors.comments = "Please provide comments for 'Yes' responses.";
    }

    // Authorization Validation
    if (!authorization.checked)
      errors.checked = "You must agree before continuing.";
    if (!authorization.dated) errors.dated = "Date is required.";
    // if (!authorization.aCity) errors.aCity = "City is required.";
    // if (!authorization.aState) errors.aState = "State is required.";
    if (!authorization.fullName) errors.fullName = "Full name is required.";

    // Send Copy Validation
    if (sendCopy && sendCopyEmail.length === 0) {
      errors.sendCopy = "Enter an email.";
    } else if (sendCopy && !isValidEmail(sendCopyEmail)) {
      errors.sendCopy = "Please enter a valid email address.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoadingSpinner(true);
    try {
      if (
        (await validateFields()) &&
        client &&
        order &&
        referenceDetail &&
        referenceFormAccess &&
        responses &&
        questionsWithOptions &&
        authorization &&
        referenceQuestionnaire &&
        signatureUrl
      ) {
        const result = await saveFinishedReferenceQuestionnaire(
          client?.clientID,
          order,
          referenceDetail,
          referenceFormAccess,
          responses,
          questionsWithOptions,
          authorization,
          comments,
          referenceQuestionnaire,
          signatureForm,
          pageDecorationForm,
          client.diocese ? client.diocese : "",
          client.diocese2 ? client.diocese2 : "",
          capacityComments,
          noKnowledgeComments,
          sendCopyEmail,
        );

        if (result) {
          await updateOrderStatus(order.orderID, 2);
          navigate("/thank-you");
        } else {
          console.error("Please fill out required fields");
        }
      } else {
        console.log("Validation failed. Please fix the errors.");
        setLoadingSpinner(false);
        const firstErrorElement = document.querySelector(".invalid-feedback");
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }
    } catch (error) {
      console.error("Error saving form:", error);
      alert("Failed to save form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div>
      <div
        className="form-container"
        style={{
          minHeight: "100vh",
          border: completingForm ? "none" : "",
        }}
      >
        {loading ? (
          <div className="loading-no-mask">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {authUrl && completingForm && (
              <>
                <div className="auth-form-container">
                  <a href={authUrl} target="_blank" rel="noopener noreferrer">
                    View the Candidate's Reference Authorization Form
                  </a>
                </div>
              </>
            )}
            <div className="questionnaire-header page-deco-container">
              {pageDecorationUrl ? (
                <div className="page-decoration">
                  <img
                    src={pageDecorationUrl}
                    alt="logo"
                    style={{
                      maxWidth: "175px",
                      maxHeight: "175px",
                      height: "auto",
                    }}
                  />
                  {!completingForm && !clientID && (
                    <ToolTip
                      tooltipText={
                        isEditing
                          ? "Logo background gets removed when questionnaire is generated and sent to reference"
                          : "Upload a new logo on the Required Forms Page."
                      }
                      placement="top"
                      className="info-icon-spec"
                    />
                  )}
                </div>
              ) : (
                !completingForm && (
                  <div className="page-decoration">
                    <div className="text">
                      <b>
                        {isEditing
                          ? "Upload a logo on the view client page in additional forms."
                          : " Upload a logo to be used here on the Required Forms Page."}
                      </b>
                    </div>
                  </div>
                )
              )}
              {client?.diocese2?.trim() && (
                <div style={{ fontWeight: "bold", textAlign: "center" }}>
                  {client.diocese2.toUpperCase()}
                </div>
              )}
              {client?.diocese && (
                <div style={{ fontWeight: "bold", textAlign: "center" }}>
                  {client.diocese.toUpperCase()}
                </div>
              )}
              <div style={{ fontSize: "1.5rem", marginTop: "5px" }}>
                Reference Questionnaire
              </div>
              {/* <div className="pop-up-warning">
                ⚠️ Application maintenance is in progress.  You can continue to
                use the application but may experience slower performance during
                this time.
              </div> */}
              {/* <div className="pop-up-error">
                🚫 We’re performing a scheduled update. Please try again later.
              </div> */}
            </div>
            <div className="text-body">
              <div>
                {new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }).format(new Date())}
              </div>
              <br></br>
              <div>
                <b>
                  {completingForm
                    ? `${referenceDetail?.placeName}`
                    : "Organization Name"}
                </b>
              </div>
              <div>
                <b>
                  {completingForm
                    ? `${referenceDetail?.referenceName}`
                    : "Reference Name"}
                </b>
              </div>
              <div>
                <b>
                  {completingForm
                    ? `${referenceDetail?.referenceEmail}`
                    : "Reference Email"}
                </b>
              </div>
              <div>
                {" "}
                <b>
                  {completingForm
                    ? `${referenceDetail?.referenceAddress}`
                    : "Reference Address"}
                </b>
              </div>
              {(!completingForm ||
                (referenceDetail?.referenceAddress2 &&
                  referenceDetail.referenceAddress2 !== "null")) && (
                <div>
                  <b>
                    {completingForm
                      ? referenceDetail?.referenceAddress2
                      : "Reference Address2"}
                  </b>
                </div>
              )}

              <div>
                <b>
                  {completingForm
                    ? `${referenceDetail?.referenceCity}`
                    : "Reference City"}
                  ,{" "}
                  {completingForm
                    ? `${referenceDetail?.referenceState}`
                    : "Reference State"}{" "}
                  {completingForm
                    ? `${referenceDetail?.referenceZipCode}`
                    : "Reference ZipCode"}
                </b>
              </div>
              <br></br>
              <div>
                Reference Request For:{" "}
                <b>
                  {completingForm
                    ? `${referenceOrder?.fullName}`
                    : "Candidate Name"}
                </b>
              </div>
              <div>
                Candidate Date of Birth:{" "}
                <b>
                  {completingForm
                    ? formatDate(order?.candidateDOB, "us", true)
                    : "DOB"}
                </b>
              </div>
              <br />
              <div>
                Dear{" "}
                <b>
                  {completingForm
                    ? referenceDetail?.referenceName?.trim()
                      ? referenceDetail.referenceName
                      : referenceDetail?.placeName?.trim()
                        ? referenceDetail.placeName
                        : ""
                    : "Reference Name"}
                </b>
                ,
              </div>
              <br />
              {referenceQuestionnaire && (
                <>
                  {(
                    [
                      "baseTextOne",
                      "baseTextTwo",
                      "baseTextThree",
                      "baseTextFour",
                      "baseTextFive",
                    ] as Array<keyof ReferenceQuestionnaire>
                  ).map((key) => {
                    const currentText =
                      (referenceQuestionnaire[key] as string) ?? "";
                    const isEmpty = currentText.trim() === "";

                    return (
                      <div
                        key={key}
                        className={`editable-text-container ${
                          isEditing ? "hover-enabled" : ""
                        }`}
                        onMouseEnter={() => isEditing && setIsHovered(key)}
                        onMouseLeave={() => isEditing && setIsHovered(null)}
                      >
                        <div
                          className={`editable-text ${
                            isEditing && isHovered === key ? "blurred" : ""
                          }`}
                        >
                          {isEmpty && isEditing ? (
                            <span className="empty-placeholder">
                              [Click to add text]
                            </span>
                          ) : (
                            <span
                              dangerouslySetInnerHTML={{ __html: currentText }}
                            />
                          )}
                        </div>

                        {isEditing && isHovered === key && (
                          <button
                            className="edit-icon"
                            onClick={() => {
                              setEditingBaseText({
                                key,
                                text: currentText,
                              });
                              setShowEditBaseText(true);
                            }}
                          >
                            {isEmpty ? (
                              <i className="bi bi-plus"></i>
                            ) : (
                              <i className="bi bi-pencil"></i>
                            )}
                          </button>
                        )}

                        {showEditBaseText &&
                          editingBaseText?.key === key &&
                          client && (
                            <EditBaseText
                              type={"reference"}
                              clientID={client.clientID}
                              baseTextKey={editingBaseText.key}
                              baseText={editingBaseText.text}
                              onClose={(updatedText, successMessage) => {
                                setReferenceQuestionnaire((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        [editingBaseText.key]: updatedText,
                                      }
                                    : prev,
                                );
                                if (successMessage) {
                                  setSuccessMessage(successMessage);
                                }
                                setShowEditBaseText(false);
                              }}
                            />
                          )}
                        <br />
                      </div>
                    );
                  })}
                </>
              )}

              <br />
              <div className="highlight-text">
                Thank you for your time and concern.
              </div>

              {client?.denomination === "Individual" ? (
                <div>Sincerely,</div>
              ) : (
                <div>Yours in Christ,</div>
              )}

              <br></br>
              <div>
                {signatureUrl ? (
                  <>
                    <div
                      style={{
                        flex: "0 0 30%",
                        alignSelf: "end",
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <img
                        src={signatureUrl}
                        alt="Client Signature"
                        style={{ maxWidth: "200px", height: "auto" }}
                      />
                      {!completingForm && (
                        <ToolTip
                          tooltipText={
                            isEditing
                              ? "Signature background gets removed when questionnaire is generated and sent to reference"
                              : "Upload a new signature on the Required Forms Page."
                          }
                          placement="top"
                          className="info-icon-spec"
                        />
                      )}
                    </div>
                    <div>
                      {primaryContact?.primaryTitle &&
                        `${primaryContact.primaryTitle} `}
                      {primaryContact?.primaryFirstName}{" "}
                      {primaryContact?.primaryLastName}
                      {primaryContact?.primaryJobTitle &&
                        `, ${primaryContact.primaryJobTitle}`}
                    </div>
                  </>
                ) : (
                  <div style={{ textDecoration: "underline" }}>
                    <b>
                      {!completingForm && (
                        <div>
                          {isEditing
                            ? "Upload a signature on the view client page in additional forms."
                            : " Upload a form signature to be used here on the Required Forms Page."}
                        </div>
                      )}
                    </b>
                  </div>
                )}
              </div>
              <br />
              <br />
              {/* YesNoText Section*/}
              {referenceQuestionnaire && (
                <div
                  className={`editable-text-container ${
                    isEditing ? "hover-enabled" : ""
                  }`}
                  onMouseEnter={() => isEditing && setIsHovered("yesNoText")}
                  onMouseLeave={() => isEditing && setIsHovered(null)}
                >
                  <div
                    className={`editable-text ${
                      isEditing && isHovered === "yesNoText" ? "blurred" : ""
                    }`}
                  >
                    {referenceQuestionnaire.yesNoText?.trim() ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: referenceQuestionnaire.yesNoText,
                        }}
                      />
                    ) : isEditing ? (
                      <span className="empty-placeholder">
                        [Click to add text]
                      </span>
                    ) : (
                      "Please check either the “Yes” or “No” box for each question..."
                    )}
                  </div>

                  {isEditing && isHovered === "yesNoText" && (
                    <button
                      className="edit-icon"
                      onClick={() => {
                        setEditingBaseText({
                          key: "yesNoText" as keyof ReferenceQuestionnaire,
                          text: referenceQuestionnaire.yesNoText || "",
                        });
                        setShowEditBaseText(true);
                      }}
                    >
                      {referenceQuestionnaire.yesNoText?.trim() ? (
                        <i className="bi bi-pencil"></i>
                      ) : (
                        <i className="bi bi-plus"></i>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* ThroughoutText Section ACNA*/}
              {referenceQuestionnaire && client?.denomination === "ACNA" && (
                <>
                  <br />
                  <br />
                  <div
                    className={`editable-text-container ${
                      isEditing ? "hover-enabled" : ""
                    }`}
                    onMouseEnter={() =>
                      isEditing && setIsHovered("throughoutText")
                    }
                    onMouseLeave={() => isEditing && setIsHovered(null)}
                  >
                    <div
                      className={`editable-text ${
                        isEditing && isHovered === "throughoutText"
                          ? "blurred"
                          : ""
                      }`}
                    >
                      {referenceQuestionnaire.throughoutText?.trim() ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: referenceQuestionnaire.throughoutText,
                          }}
                        />
                      ) : isEditing ? (
                        <span className="empty-placeholder">
                          [Click to add text]
                        </span>
                      ) : (
                        "*Throughout this document, “charged” indicates allegations made in writing and known to you."
                      )}
                    </div>

                    {isEditing && isHovered === "throughoutText" && (
                      <button
                        className="edit-icon"
                        onClick={() => {
                          setEditingBaseText({
                            key: "throughoutText" as keyof ReferenceQuestionnaire,
                            text: referenceQuestionnaire.throughoutText || "",
                          });
                          setShowEditBaseText(true);
                        }}
                      >
                        {referenceQuestionnaire.throughoutText?.trim() ? (
                          <i className="bi bi-pencil"></i>
                        ) : (
                          <i className="bi bi-plus"></i>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
              <br></br>
              <br />
              <div>
                In what capacity have you known this person? (please include
                relevant dates)
              </div>
              <div>
                <textarea
                  className="form-control"
                  value={capacityComments}
                  disabled={!completingForm}
                  onChange={(e) => handleCommentsChange(e, "capacity")}
                  style={{ height: "100px" }}
                  maxLength={300}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                    opacity: !completingForm ? 0.6 : 1,
                  }}
                >
                  {capacityComments?.length ?? 0}/300
                </div>
              </div>
              <br />
              <div>
                If you have no knowledge of this person and/or do not have
                access to records, please indicate by stating below and
                selecting all “X” options.
              </div>
              <div>
                <textarea
                  className="form-control"
                  value={noKnowledgeComments}
                  disabled={!completingForm}
                  onChange={(e) => handleCommentsChange(e, "noKnowledge")}
                  style={{ height: "100px" }}
                  maxLength={300}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                    opacity: !completingForm ? 0.6 : 1,
                  }}
                >
                  {noKnowledgeComments?.length ?? 0}/300
                </div>
              </div>

              {/* ThroughoutText Section*/}
              {referenceQuestionnaire && client?.denomination !== "ACNA" && (
                <>
                  <br />
                  <div
                    className={`editable-text-container ${
                      isEditing ? "hover-enabled" : ""
                    }`}
                    onMouseEnter={() =>
                      isEditing && setIsHovered("throughoutText")
                    }
                    onMouseLeave={() => isEditing && setIsHovered(null)}
                  >
                    <div
                      className={`editable-text ${
                        isEditing && isHovered === "throughoutText"
                          ? "blurred"
                          : ""
                      }`}
                    >
                      {referenceQuestionnaire.throughoutText?.trim() ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: referenceQuestionnaire.throughoutText,
                          }}
                        />
                      ) : isEditing ? (
                        <span className="empty-placeholder">
                          [Click to add text]
                        </span>
                      ) : (
                        "*Throughout this document, “charged” indicates allegations made in writing and known to you."
                      )}
                    </div>

                    {isEditing && isHovered === "throughoutText" && (
                      <button
                        className="edit-icon"
                        onClick={() => {
                          setEditingBaseText({
                            key: "throughoutText" as keyof ReferenceQuestionnaire,
                            text: referenceQuestionnaire.throughoutText || "",
                          });
                          setShowEditBaseText(true);
                        }}
                      >
                        {referenceQuestionnaire.throughoutText?.trim() ? (
                          <i className="bi bi-pencil"></i>
                        ) : (
                          <i className="bi bi-plus"></i>
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
              <br></br>

              {/* Edit Modal for YesNoText & ThroughoutText */}
              {showEditBaseText &&
                editingBaseText !== null &&
                ["yesNoText", "throughoutText"].includes(editingBaseText.key) &&
                client && (
                  <EditBaseText
                    type={"reference"}
                    clientID={client.clientID}
                    baseTextKey={
                      editingBaseText.key as keyof ReferenceQuestionnaire
                    }
                    baseText={editingBaseText.text}
                    onClose={(updatedText, successMessage) => {
                      setReferenceQuestionnaire((prev) =>
                        prev
                          ? {
                              ...prev,
                              [editingBaseText.key]: updatedText,
                            }
                          : prev,
                      );
                      if (successMessage) {
                        setSuccessMessage(successMessage);
                      }
                      setShowEditBaseText(false);
                    }}
                  />
                )}

              <br></br>
              <div>
                Click "X" or "No" heading to select this response for all
                questions.
              </div>
              {validationErrors.questions && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.questions}
                </div>
              )}
              <br />
              <div className="grid-styles">
                <div></div>
                <div
                  className="option-style"
                  onClick={() => setShowConfirmationSelectXAll(true)}
                >
                  X
                </div>
                <div
                  className="option-style"
                  onClick={() => setShowConfirmationSelectNoAll(true)}
                >
                  No
                </div>
                <div className="option-style">Yes</div>
              </div>
              {showConfirmationSelectXAll && (
                <ConfirmPopup
                  onConfirm={() => {
                    handleSelectAll("X");
                    setShowConfirmationSelectXAll(false);
                  }}
                  onCancel={() => setShowConfirmationSelectXAll(false)}
                  message="Are you sure you would like to select 'X' for all?"
                />
              )}
              {showConfirmationSelectNoAll && (
                <ConfirmPopup
                  onConfirm={() => {
                    handleSelectAll("No");
                    setShowConfirmationSelectNoAll(false);
                  }}
                  onCancel={() => setShowConfirmationSelectNoAll(false)}
                  message="Are you sure you would like to select 'No' for all?"
                />
              )}

              <ol style={{ listStyleType: "decimal", paddingTop: "20px" }}>
                {Array.from(questionsWithOptions.entries()).map(
                  ([questionID, { questionText, options }]) => {
                    const hasOptions = options.length > 0;

                    const questionGridClass = [
                      "questionRow",
                      isEditing ? "isEditing" : "isViewing",
                      hasOptions ? "hasOptions" : "noOptions",
                    ].join(" ");

                    const optionGridClass = [
                      "optionRow",
                      isEditing ? "isEditing" : "isViewing",
                    ].join(" ");

                    return (
                      <li key={questionID} style={{ marginBottom: "1rem" }}>
                        <div className={questionGridClass}>
                          {isEditing && (
                            <div>
                              <button
                                className="edit-icon-inline"
                                onClick={() => {
                                  setEditingItem({
                                    id: questionID,
                                    text: questionText,
                                    type: "question",
                                  });
                                  setShowEditModal(true);
                                }}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            </div>
                          )}
                          <span style={{ maxWidth: "85%" }}>
                            {questionText}
                          </span>
                          {options.length === 0 &&
                            !hiddenYesNoQuestionIds.has(questionID) && (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    name={`question-${questionID}`}
                                    value="X"
                                    checked={
                                      responses[`q-${questionID}`] === "X"
                                    }
                                    onChange={(e) =>
                                      handleResponseChange(e, `q-${questionID}`)
                                    }
                                    disabled={!completingForm}
                                  />
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`question-${questionID}`}
                                    value="No"
                                    checked={
                                      responses[`q-${questionID}`] === "No"
                                    }
                                    onChange={(e) =>
                                      handleResponseChange(e, `q-${questionID}`)
                                    }
                                    disabled={!completingForm}
                                  />
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`question-${questionID}`}
                                    value="Yes"
                                    checked={
                                      responses[`q-${questionID}`] === "Yes"
                                    }
                                    onChange={(e) =>
                                      handleResponseChange(e, `q-${questionID}`)
                                    }
                                    disabled={!completingForm}
                                  />
                                </label>
                              </>
                            )}
                        </div>

                        {options.length > 0 && (
                          <ol className="options-container">
                            {options.map((option) => (
                              <li key={option.questionOptionID}>
                                <div className={optionGridClass}>
                                  {isEditing && (
                                    <div>
                                      <button
                                        className="edit-icon-inline"
                                        onClick={() => {
                                          setEditingItem({
                                            id: option.questionOptionID,
                                            text: option.questionOptionText,
                                            type: "option",
                                          });
                                          setShowEditModal(true);
                                        }}
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                    </div>
                                  )}
                                  <span>{option.questionOptionText}</span>
                                  <label>
                                    <input
                                      type="radio"
                                      name={`option-${option.questionOptionID}`}
                                      value="X"
                                      checked={
                                        responses[
                                          `o-${option.questionOptionID}`
                                        ] === "X"
                                      }
                                      onChange={(e) =>
                                        handleResponseChange(
                                          e,
                                          `o-${option.questionOptionID}`,
                                        )
                                      }
                                      disabled={!completingForm}
                                    />
                                  </label>
                                  <label>
                                    <input
                                      type="radio"
                                      name={`option-${option.questionOptionID}`}
                                      value="No"
                                      checked={
                                        responses[
                                          `o-${option.questionOptionID}`
                                        ] === "No"
                                      }
                                      onChange={(e) =>
                                        handleResponseChange(
                                          e,
                                          `o-${option.questionOptionID}`,
                                        )
                                      }
                                      disabled={!completingForm}
                                    />
                                  </label>
                                  <label>
                                    <input
                                      type="radio"
                                      name={`option-${option.questionOptionID}`}
                                      value="Yes"
                                      checked={
                                        responses[
                                          `o-${option.questionOptionID}`
                                        ] === "Yes"
                                      }
                                      onChange={(e) =>
                                        handleResponseChange(
                                          e,
                                          `o-${option.questionOptionID}`,
                                        )
                                      }
                                      disabled={!completingForm}
                                    />
                                  </label>
                                </div>
                              </li>
                            ))}
                          </ol>
                        )}
                      </li>
                    );
                  },
                )}
              </ol>
              {showEditModal && editingItem && (
                <EditQuestion
                  questionnaireType={"reference"}
                  itemID={editingItem.id}
                  text={editingItem.text}
                  type={editingItem.type}
                  onClose={() => window.location.reload()}
                />
              )}

              {isEditing && (
                <div className="filter-actions">
                  <button
                    className={`oxbtn btn-new`}
                    onClick={() => {
                      setShowAddModal(true);
                    }}
                  >
                    <div> Add Question </div>
                    <i className="bi bi-plus-circle-fill plus-icon"></i>
                  </button>
                </div>
              )}

              {showAddModal && referenceQuestionnaire && (
                <AddQuestion
                  type={"reference"}
                  questionnaireID={
                    referenceQuestionnaire?.referenceQuestionnaireID
                  }
                  onClose={() => window.location.reload()}
                />
              )}

              <br></br>
              <div className="text-body">
                If you answered 'Yes' to any question, please list the question
                number(s), provide relevant details, and explain how the issue
                was resolved. You may also use the comments section below to
                provide any additional information or context, as well as
                positive comments.
              </div>
              <br></br>
              <div>Comments/Explanations</div>
              <div>
                <textarea
                  className="form-control"
                  value={comments}
                  disabled={!completingForm}
                  onChange={(e) => handleCommentsChange(e, "comments")}
                  style={{ height: "100px" }}
                  maxLength={4000}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                    opacity: !completingForm ? 0.6 : 1,
                  }}
                >
                  {comments?.length ?? 0}/4000
                </div>
              </div>
              {validationErrors.comments && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.comments}
                </div>
              )}
              <br />

              {completingForm && (
                <AuthorizationForm
                  authorization={authorization}
                  validationErrors={validationErrors}
                  onInputChange={handleAuthorizationChange}
                  type={""}
                />
              )}

              {completingForm && (
                <>
                  <div className="send-copy-container">
                    <div className="field-long" style={{ flex: "0 0 10%" }}>
                      <div>
                        <input
                          className={`form-check-input`}
                          type="checkbox"
                          checked={sendCopy || false}
                          onChange={(e) => setSendCopy(!sendCopy)}
                        />
                      </div>
                      <label className="field-label">
                        Check this box to receive a copy of your response by
                        email
                      </label>
                    </div>
                    <div className="field-stacked" style={{ flex: "0 0 35%" }}>
                      <div className="field-input">
                        <input
                          className={`form-control ${
                            validationErrors.sendCopy ? "is-invalid" : ""
                          }`}
                          type="text"
                          value={sendCopyEmail || ""}
                          placeholder="Enter an email address"
                          onChange={(e) =>
                            handleSendCopyChange(e, "sendCopyEmail")
                          }
                        />
                        {validationErrors.sendCopy && (
                          <div className="invalid-feedback">
                            {validationErrors.sendCopy}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="btn-center">
                    <button
                      className="oxbtn btn-green"
                      onClick={() => handleSave()}
                    >
                      Submit
                    </button>
                  </div>
                </>
              )}
              {loadingSpinner && (
                <div className="loading-mask">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>
          </>
        )}
        {successMessage && (
          <div className="alert-message alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage("")}
              aria-label="Close"
            ></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceForm;
